import { createServerFn } from "@tanstack/react-start";
import { generateText } from "ai";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { getTutorModel } from "./ai-gateway.server";

/**
 * AI Adaptive Learning Engine.
 * Runs after every quiz/practice/assignment/exam submission.
 * Produces: structured analysis, tiered recommendations, mastery update,
 * and (optionally) a parent/teacher intervention flag for repeated struggles.
 */

const QuestionResult = z.object({
  prompt: z.string(),
  correct: z.boolean().nullable(),
  skipped: z.boolean().default(false),
  topic: z.string().optional(),
  difficulty: z.string().optional(),
  time_sec: z.number().optional(),
  learner_answer: z.string().optional(),
  correct_answer: z.string().optional(),
});

const InputSchema = z.object({
  learnerId: z.string().uuid(),
  sourceType: z.enum(["quiz", "practice", "assignment", "exam"]),
  sourceId: z.string().optional(),
  subject: z.string().min(1),
  topic: z.string().min(1),
  level: z.string().optional(), // P5, S2, etc
  percent: z.number().min(0).max(100),
  durationSec: z.number().int().nonnegative().default(0),
  totalQuestions: z.number().int().nonnegative(),
  correctCount: z.number().int().nonnegative(),
  incorrectCount: z.number().int().nonnegative(),
  skippedCount: z.number().int().nonnegative().default(0),
  questions: z.array(QuestionResult).default([]),
});

export type AdaptiveTier = "excellent" | "great" | "progress" | "recovery";
export type MasteryLevel = "beginning" | "developing" | "proficient" | "mastered";

export type AdaptiveAnalysis = {
  tier: AdaptiveTier;
  masteryLevel: MasteryLevel;
  headline: string;
  narrative: string; // 2-4 sentence friendly explanation
  strongTopics: string[];
  weakTopics: string[];
  commonMistakes: string[];
  knowledgeGaps: string[];
  timeManagement: string;
  guessingFlag: boolean;
  recommendations: {
    nextLesson: string;
    revisionVideo: string;
    notes: string;
    pastPapers: string;
    tutorPrompt: string;
    estimatedMinutes: number;
    confidence: "low" | "medium" | "high";
  };
  nextActions: string[];
  ctaLabel: string;
};

export type AdaptiveReport = AdaptiveAnalysis & {
  reportId: string;
  percent: number;
  totals: { correct: number; incorrect: number; skipped: number; total: number; durationSec: number };
  topicBreakdown: Array<{ topic: string; correct: number; total: number; percent: number }>;
  difficultyBreakdown: Array<{ difficulty: string; correct: number; total: number; percent: number }>;
};

function tierFor(p: number): AdaptiveTier {
  if (p >= 90) return "excellent";
  if (p >= 75) return "great";
  if (p >= 50) return "progress";
  return "recovery";
}

function ctaFor(t: AdaptiveTier): string {
  return t === "excellent" ? "Challenge Me" : t === "great" ? "Practice More" : t === "progress" ? "Revise This Topic" : "Start My Recovery Plan";
}

function tryParseJson(raw: string): unknown {
  const cleaned = raw.replace(/^```(?:json)?\s*/i, "").replace(/```\s*$/i, "").trim();
  try { return JSON.parse(cleaned); } catch {
    const m = cleaned.match(/\{[\s\S]*\}$/);
    if (m) { try { return JSON.parse(m[0]); } catch { /* */ } }
    return null;
  }
}

function rollupBy<T extends { topic?: string; difficulty?: string; correct: boolean | null }>(items: T[], key: "topic" | "difficulty") {
  const buckets = new Map<string, { correct: number; total: number }>();
  for (const it of items) {
    const k = (it[key] || "").toString().trim();
    if (!k) continue;
    const b = buckets.get(k) ?? { correct: 0, total: 0 };
    b.total += 1;
    if (it.correct === true) b.correct += 1;
    buckets.set(k, b);
  }
  return Array.from(buckets.entries()).map(([name, b]) => ({
    [key]: name, correct: b.correct, total: b.total, percent: b.total ? +((b.correct / b.total) * 100).toFixed(1) : 0,
  })) as Array<{ topic?: string; difficulty?: string; correct: number; total: number; percent: number }>;
}

export const analyzeAssessment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => InputSchema.parse(d))
  .handler(async ({ data, context }): Promise<AdaptiveReport> => {
    const { supabase } = context;
    const tier = tierFor(data.percent);

    // Build per-question summary (truncated)
    const sample = data.questions.slice(0, 25).map((q, i) => ({
      n: i + 1,
      topic: q.topic ?? data.topic,
      difficulty: q.difficulty ?? "—",
      correct: q.correct,
      skipped: q.skipped,
      time_sec: q.time_sec ?? null,
      learner: (q.learner_answer ?? "").slice(0, 80),
      expected: (q.correct_answer ?? "").slice(0, 80),
      prompt: q.prompt.slice(0, 120),
    }));

    const system = `You are MAKAI AI, a kind Ugandan tutor. Analyse assessment results with empathy.
ALWAYS encouraging. Never say "you failed" — say "you've identified an area to grow".
Return ONLY a JSON object. No prose, no markdown.`;

    const prompt = `Learner just finished a ${data.sourceType} on "${data.topic}" (${data.subject}${data.level ? ", " + data.level : ""}).
Score: ${data.correctCount}/${data.totalQuestions} (${data.percent}%). Time: ${data.durationSec}s. Skipped: ${data.skippedCount}.
Tier band: ${tier}.

Per-question results:
${JSON.stringify(sample)}

Produce JSON of this exact shape:
{
  "headline": "string (short, motivating)",
  "narrative": "2-4 sentences plain English, friendly, specific to what they got right/wrong",
  "strongTopics": ["..."],
  "weakTopics": ["..."],
  "commonMistakes": ["..."],
  "knowledgeGaps": ["..."],
  "timeManagement": "one sentence — rushed, balanced, slow, or N/A",
  "guessingFlag": true|false,
  "masteryLevel": "beginning|developing|proficient|mastered",
  "recommendations": {
    "nextLesson": "concrete lesson title or concept to study next",
    "revisionVideo": "topic/keyword to search for video",
    "notes": "specific notes/section to review",
    "pastPapers": "exam paper / year / question type to attempt",
    "tutorPrompt": "a question to ask MAKAI AI tutor",
    "estimatedMinutes": 20,
    "confidence": "low|medium|high"
  },
  "nextActions": ["3-5 concrete next steps"]
}`;

    let ai: Partial<AdaptiveAnalysis> = {};
    try {
      const model = getTutorModel("google/gemini-3-flash-preview");
      const { text } = await generateText({
        model,
        messages: [{ role: "system", content: system }, { role: "user", content: prompt }],
      });
      const parsed = tryParseJson(text) as Partial<AdaptiveAnalysis> | null;
      if (parsed) ai = parsed;
    } catch { /* fall through to defaults */ }

    // Fallbacks
    const masteryLevel: MasteryLevel = (ai.masteryLevel as MasteryLevel) ?? (data.percent >= 90 ? "mastered" : data.percent >= 75 ? "proficient" : data.percent >= 50 ? "developing" : "beginning");
    const analysis: AdaptiveAnalysis = {
      tier,
      masteryLevel,
      headline: ai.headline ?? (tier === "excellent" ? "🎉 Excellent work!" : tier === "great" ? "Great progress!" : tier === "progress" ? "You're making progress — let's sharpen this." : "Let's master this topic together."),
      narrative: ai.narrative ?? `You scored ${data.percent}% on ${data.topic}. Keep practising — every attempt makes you stronger.`,
      strongTopics: ai.strongTopics ?? [],
      weakTopics: ai.weakTopics ?? (data.percent < 75 ? [data.topic] : []),
      commonMistakes: ai.commonMistakes ?? [],
      knowledgeGaps: ai.knowledgeGaps ?? [],
      timeManagement: ai.timeManagement ?? "Balanced pace.",
      guessingFlag: Boolean(ai.guessingFlag),
      recommendations: {
        nextLesson: ai.recommendations?.nextLesson ?? `Review the core concepts of ${data.topic}`,
        revisionVideo: ai.recommendations?.revisionVideo ?? `${data.topic} explained`,
        notes: ai.recommendations?.notes ?? `${data.subject} notes on ${data.topic}`,
        pastPapers: ai.recommendations?.pastPapers ?? `Past paper questions on ${data.topic}`,
        tutorPrompt: ai.recommendations?.tutorPrompt ?? `Can you explain ${data.topic} step by step?`,
        estimatedMinutes: ai.recommendations?.estimatedMinutes ?? (tier === "recovery" ? 45 : tier === "progress" ? 30 : 15),
        confidence: ai.recommendations?.confidence ?? (tier === "excellent" ? "high" : tier === "great" ? "medium" : "low"),
      },
      nextActions: ai.nextActions ?? [],
      ctaLabel: ctaFor(tier),
    };

    // Topic & difficulty rollups (from raw questions)
    const topicBreakdown = (rollupBy(data.questions, "topic") as Array<{ topic: string; correct: number; total: number; percent: number }>);
    const difficultyBreakdown = (rollupBy(data.questions, "difficulty") as Array<{ difficulty: string; correct: number; total: number; percent: number }>);

    // Persist mastery
    try {
      const { data: existing } = await supabase
        .from("topic_mastery")
        .select("id,score_avg,attempts,high_scores")
        .eq("learner_id", data.learnerId).eq("subject", data.subject).eq("topic", data.topic)
        .maybeSingle();
      const attempts = (existing?.attempts ?? 0) + 1;
      const prevAvg = Number(existing?.score_avg ?? 0);
      const score_avg = +(((prevAvg * (attempts - 1)) + data.percent) / attempts).toFixed(2);
      const high_scores = (existing?.high_scores ?? 0) + (data.percent >= 85 ? 1 : 0);
      const finalLevel: MasteryLevel =
        high_scores >= 2 && score_avg >= 85 ? "mastered"
        : score_avg >= 75 ? "proficient"
        : score_avg >= 50 ? "developing"
        : "beginning";
      await supabase.from("topic_mastery").upsert({
        learner_id: data.learnerId,
        subject: data.subject,
        topic: data.topic,
        level: finalLevel,
        score_avg,
        attempts,
        high_scores,
        last_percent: data.percent,
        last_assessed_at: new Date().toISOString(),
        mastered_at: finalLevel === "mastered" ? new Date().toISOString() : null,
      }, { onConflict: "learner_id,subject,topic" });
      analysis.masteryLevel = finalLevel;
    } catch { /* ignore */ }

    // Insert report
    const { data: inserted } = await supabase.from("adaptive_reports").insert({
      learner_id: data.learnerId,
      source_type: data.sourceType,
      source_id: data.sourceId ?? null,
      subject: data.subject,
      topic: data.topic,
      level: data.level ?? null,
      percent: data.percent,
      tier,
      mastery_level: analysis.masteryLevel,
      analysis,
      recommendations: analysis.recommendations,
    }).select("id").single();

    // Intervention flag — recovery tier OR two failures in a row
    if (tier === "recovery") {
      try {
        const { count: recentFailCount } = await supabase
          .from("adaptive_reports")
          .select("id", { count: "exact", head: true })
          .eq("learner_id", data.learnerId).eq("topic", data.topic).eq("tier", "recovery");
        if ((recentFailCount ?? 0) >= 1) {
          await supabase.from("learner_interventions").insert({
            learner_id: data.learnerId,
            subject: data.subject,
            topic: data.topic,
            reason: `Scored ${data.percent}% on ${data.topic} — repeated struggle`,
            suggested_action: analysis.recommendations.nextLesson,
            study_minutes: analysis.recommendations.estimatedMinutes,
            support_plan: {
              video: analysis.recommendations.revisionVideo,
              notes: analysis.recommendations.notes,
              tutor: analysis.recommendations.tutorPrompt,
              nextActions: analysis.nextActions,
            },
          });
        }
      } catch { /* ignore */ }
    }

    return {
      ...analysis,
      reportId: inserted?.id ?? "",
      percent: data.percent,
      totals: {
        correct: data.correctCount,
        incorrect: data.incorrectCount,
        skipped: data.skippedCount,
        total: data.totalQuestions,
        durationSec: data.durationSec,
      },
      topicBreakdown,
      difficultyBreakdown,
    };
  });
